import React, {useEffect, useState} from 'react';
import {DateTime, DurationObject} from 'luxon';

import {FooterButtonProps} from '../modal/components/footer/modal-footer';
import {
    School
} from '../../api/types';
import {DialogModal, DialogModalProps} from '../modal/modal';
import {ChangeEndYearView} from './components/change-end-year-view';
import {LoadingView} from '../loading/loading';
import {getNextEndDate} from '../../containers/home/utils/end-date-validation';

export type AcademicYearChangeModalProps =
    & DialogModalProps
    & {
    isLoading?: boolean
    school: School | null
    onClose: () => any
    onSubmitChangeEndDate: (input: DateTime) => any
}

export type DateChangeDifference = DateTime | DurationObject | null;

export const AcademicYearChangeModal = ({
    onClose,
    onSubmitChangeEndDate,
    school,
    headerTitle = 'Current academic year',
    isLoading = false,
    ...restProps
}: AcademicYearChangeModalProps) => {
    const [endDate, setEndDate] = useState<DateTime | null>(null);
    const [isAcademicYear, setIsAcademicYear] = useState(false);
    const [isAcademicYearLess, setIsAcademicYearLess] = useState(false);
    const [dateChanged, setDateChanged] = useState<DateChangeDifference>(null);
    useEffect(() => {
        const initialEndDate = school?.academicYear?.endDate ?
            DateTime.fromISO(school.academicYear.endDate, {zone: 'utc'}) :
            null;

        setEndDate(initialEndDate);
    }, [school?.schoolId, school?.academicYear?.endDate]);

    const handleEndDateInputChange = (endDateTime: DateTime) => {
        const startDateTime = DateTime.fromISO(school?.academicYear?.startDate || '', {zone: 'utc'});
        const dateDiff = endDateTime
            ? endDateTime.diff(startDateTime, ['years', 'months', 'weeks']).toObject()
            : getNextEndDate(startDateTime);
        const isAcademicYearAchieved = endDateTime.diff(startDateTime, ['days']);
        setIsAcademicYear(Math.ceil(isAcademicYearAchieved.days) === 365);
        setIsAcademicYearLess(Math.ceil(isAcademicYearAchieved.days) < 365);
        setDateChanged(dateDiff);
        setEndDate(endDateTime);
    };

    const handleEndDateChange = () => {
        if (!endDate) {
            return;
        }
        onSubmitChangeEndDate(endDate);
    };

    const buttons: Array<FooterButtonProps> = [
        {
            'data-testid': 'ChangeDateButton',
            labelText: 'Change Date',
            disabled: school?.hasFutureSchoolAcademicYear,
            onClick: handleEndDateChange
        },
        {
            'data-testid': 'CancelButton',
            labelText: 'Cancel',
            onClick: onClose,
            variant: 'outlined',
            disabled: false
        }
    ];

    if (!school) {
        return null;
    }

    return (
        <DialogModal
            onClose={onClose}
            onBackdropClick={onClose}
            onEscapeKeyDown={onClose}
            buttons={isLoading ? [] : buttons}
            headerTitle={isLoading ? undefined : headerTitle}
            showCloseButton={!isLoading}
            data-testid={'AcademicYearChangeModal'}
            disableEnforceFocus={true}
            disableAutoFocus={true}
            {...restProps}
        >
            {school && !isLoading ?
                <ChangeEndYearView
                    endDate={endDate}
                    school={school}
                    onEndDateChange={handleEndDateInputChange}
                    dateChangeDifference={dateChanged}
                    isAcademicYear={isAcademicYear}
                    isAcademicYearLess={isAcademicYearLess}
                /> :
                null}

            {isLoading ?
                <LoadingView loadingText={'Submitting Changes...'}/> :
                null}
        </DialogModal>
    );
};
